import React, { Fragment, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { VocabularyIrregularVerb } from 'app/types';
import AudioButton from 'components/dictionaryView/AudioButton';
import { DictionaryWord } from 'app/types';
import StatusCell from 'components/vocabularyWordsView/StatusCell';
import GroupHeader from 'components/vocabularyWordsView/GroupHeader';
import {
  GroupedWords,
  UseVocabularyWordsViewState,
} from 'components/vocabularyWordsView/useVocabularyWordsView';
import { Checkbox, LevelPill } from './primitives';
import { WordStatus, hexToRgba, statusColor, statusOfVerb } from './meta';
import { UseVocabularyVerbsViewState } from './useVocabularyVerbsView';

interface VerbWorkCardsProps {
  view: UseVocabularyVerbsViewState;
  onSetStatus: (id: number, status: WordStatus) => void;
  onDeleteOne: (id: number) => void;
}

const FormBlock: React.FC<{
  label: string;
  word: string;
  transcription?: string;
  strong?: boolean;
  primary?: boolean;
}> = ({ label, word, transcription, strong, primary }) => (
  <Box
    sx={{
      minWidth: 0,
      px: 1.5,
      py: 1.25,
      bgcolor: 'background.default',
      borderRadius: 1,
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Box
      sx={{
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: primary ? 'primary.main' : 'text.secondary',
        mb: 0.75,
      }}
    >
      {label}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875 }}>
      <AudioButton word={word} size={26} />
      <Box
        sx={{
          fontSize: 15,
          fontWeight: strong ? 800 : 700,
          color: primary ? 'primary.main' : 'text.primary',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {word}
      </Box>
    </Box>
    {transcription ? (
      <Box
        sx={{
          fontSize: 12,
          color: 'text.secondary',
          fontFamily: 'ui-monospace, monospace',
          mt: 0.5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {transcription}
      </Box>
    ) : null}
  </Box>
);

const VerbCard: React.FC<{
  verb: VocabularyIrregularVerb;
  view: UseVocabularyVerbsViewState;
  onSetStatus: (id: number, status: WordStatus) => void;
}> = ({ verb, view, onSetStatus }) => {
  const theme = useTheme();
  const mode: 'light' | 'dark' = theme.palette.mode === 'dark' ? 'dark' : 'light';
  const [hover, setHover] = useState(false);
  const selected = !!view.selected[verb.id];
  const s = statusOfVerb(verb);
  const accent =
    s === 'ACTIVE'
      ? statusColor('ACTIVE', mode)
      : s === 'LEARNED'
      ? statusColor('LEARNED', mode)
      : 'transparent';
  const highlighted = verb.id === view.highlightId;
  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 2,
        borderRadius: 1.5,
        bgcolor: highlighted
          ? hexToRgba('#27CCBC', 0.12)
          : selected
          ? 'primary.body'
          : 'background.paper',
        border: '1px solid',
        borderColor: selected ? 'primary.main' : hover ? 'primary.tableBorder' : 'divider',
        borderLeft: '4px solid',
        borderLeftColor: accent === 'transparent' ? 'divider' : accent,
        transition: 'all .14s ease',
      }}
    >
      {/* header: checkbox + translation + level + status */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <Box sx={{ pt: 0.25 }}>
          <Checkbox checked={selected} onClick={() => view.toggle(verb.id)} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}>
            {verb.translation}
          </Box>
          {verb.levelBased ? (
            <Box sx={{ mt: 0.75 }}>
              <LevelPill level={verb.levelBased} size={'sm'} />
            </Box>
          ) : null}
        </Box>
        <StatusCell
          word={{ id: verb.id, status: s } as unknown as DictionaryWord}
          onSetStatus={onSetStatus}
        />
      </Box>

      {/* three form blocks */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
          gap: 1,
        }}
      >
        <FormBlock label={'Infinitive'} word={verb.infinitive} transcription={verb.infinitiveTranscription} strong primary />
        <FormBlock label={'Past Simple'} word={verb.pastSimple} transcription={verb.pastSimpleTranscription} />
        <FormBlock label={'Past Participle'} word={verb.pastParticiple} transcription={verb.pastParticipleTranscription} />
      </Box>

      {verb.description ? (
        <Box sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.45 }}>
          {verb.description}
        </Box>
      ) : null}
    </Box>
  );
};

const VerbWorkCards: React.FC<VerbWorkCardsProps> = ({
  view,
  onSetStatus,
  onDeleteOne: _onDeleteOne,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const cols = isPhone ? 1 : isTablet ? 2 : view.settings.cardsPerRow;

  if (view.filtered.length === 0) {
    return (
      <Box
        sx={{
          color: 'text.secondary',
          px: 3,
          py: 6,
          textAlign: 'center',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
        }}
      >
        Нічого не знайдено. Спробуй змінити фільтри або пошук.
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: 1.75,
      }}
    >
      {view.grouped.map((g) => (
        <Fragment key={g.key}>
          {g.label !== null && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <GroupHeader
                group={{
                  key: g.key,
                  label: g.label,
                  words: g.verbs,
                  ids: g.ids,
                } as unknown as GroupedWords}
                view={view as unknown as UseVocabularyWordsViewState}
                span
              />
            </Box>
          )}
          {g.verbs.map((v) => (
            <VerbCard key={v.id} verb={v} view={view} onSetStatus={onSetStatus} />
          ))}
        </Fragment>
      ))}
    </Box>
  );
};

export default VerbWorkCards;
